import type { LucideIcon } from 'lucide-react';
import {
    Building2,
    Car,
    GraduationCap,
    HeartPulse,
    Home as HomeIcon,
    Landmark,
    Scale,
    ShoppingBag,
    ShoppingCart,
    Sparkles,
} from 'lucide-react';

export interface IndustryInfo {
    slug: string;
    icon: LucideIcon;
    name: string;
    tagline: string;
    description: string;
    bullets: string[];
}

/** The 10 industries AIPA ships starting templates for — single source of truth for the header mega-menu and each industry's detail page. */
export const INDUSTRIES: IndustryInfo[] = [
    {
        slug: 'real-estate',
        icon: HomeIcon,
        name: 'Real Estate',
        tagline: 'Qualify buyers and renters instantly, any time of day',
        description:
            'Property inquiries come in at all hours, and the first agency to respond usually wins the lead. AIPA’s assistant greets every visitor, asks the qualifying questions a good agent would — buy or rent, budget, neighborhood, timeline — and hands your team a warm, scored lead instead of a cold form submission.',
        bullets: [
            'Branching flows for buy / rent / property management, out of the box',
            'Instant scheduling for showings, synced to your team’s calendar',
            'Every inquiry scored and routed to the right agent automatically',
        ],
    },
    {
        slug: 'senior-living',
        icon: Building2,
        name: 'Senior Living',
        tagline: 'Help families find the right community, faster',
        description:
            'Families searching for senior care are often stressed and time-pressed, comparing several communities at once. AIPA answers care-level and pricing questions immediately, and Pricing Assistant keeps your rates positioned correctly against every community you compete with — so you’re the easiest one to say yes to.',
        bullets: [
            'Guided care-level questions (independent living, assisted living, memory care)',
            'Live competitor rate comparisons via Pricing Assistant',
            'Reputation monitoring across every review source families check',
        ],
    },
    {
        slug: 'healthcare',
        icon: HeartPulse,
        name: 'Healthcare',
        tagline: 'Answer patient questions and book appointments automatically',
        description:
            'Patients expect fast answers about insurance, availability, and services — and a missed call is often a missed patient. AIPA handles routine questions and appointment requests around the clock, while Sentiment Analysis flags any patient-experience issue in reviews before it becomes a reputation problem.',
        bullets: [
            'Answers insurance, services, and hours questions without staff involvement',
            'Appointment requests booked straight into your calendar',
            'Automatic alerts the moment a review signals a patient concern',
        ],
    },
    {
        slug: 'automotive',
        icon: Car,
        name: 'Automotive',
        tagline: 'Turn showroom visitors into scheduled test drives',
        description:
            'Car shoppers research online long before they call — AIPA meets them there, answering inventory and financing questions instantly and booking test drives without a salesperson lifting a finger. Every conversation becomes a lead in your CRM, already qualified by budget and vehicle interest.',
        bullets: [
            'Inventory and trade-in questions answered instantly, day or night',
            'Test drives booked directly into your team’s schedule',
            'Leads pre-qualified by budget, model, and timeline before handoff',
        ],
    },
    {
        slug: 'hospitality',
        icon: Sparkles,
        name: 'Hospitality',
        tagline: 'Handle booking questions around the clock',
        description:
            'Guests want instant answers about availability, amenities, and policies — especially outside your front-desk hours. AIPA covers those questions automatically and routes booking requests straight to your team, while Sentiment Analysis keeps a constant read on guest satisfaction across every review platform.',
        bullets: [
            'Round-the-clock answers on availability, amenities, and policies',
            'Booking requests routed straight to your reservations team',
            'Guest sentiment tracked across every review source, in one place',
        ],
    },
    {
        slug: 'retail',
        icon: ShoppingBag,
        name: 'Retail',
        tagline: 'Guide shoppers to the right product, instantly',
        description:
            'In-store or online, shoppers who can’t get a quick answer often leave without buying. AIPA acts as a knowledgeable assistant on your site — checking stock questions, comparing options, and handing off ready-to-buy shoppers as scored leads for your sales team to follow up with.',
        bullets: [
            'Product and stock questions answered without staff involvement',
            'Guided comparisons that steer shoppers to the right fit',
            'Every serious shopper captured as a lead, not lost to a closed tab',
        ],
    },
    {
        slug: 'ecommerce',
        icon: ShoppingCart,
        name: 'Ecommerce',
        tagline: 'Recover carts and answer order questions live',
        description:
            'Cart abandonment and order-status questions are two of the biggest drains on an ecommerce team’s time. AIPA answers shipping, sizing, and order questions instantly, nudges hesitant shoppers toward checkout, and captures every unresolved question as a lead so nothing falls through the cracks.',
        bullets: [
            'Instant answers on shipping, sizing, and order status',
            'Guided prompts that reduce cart abandonment',
            'Unresolved questions captured and routed to your support team',
        ],
    },
    {
        slug: 'legal',
        icon: Scale,
        name: 'Legal',
        tagline: 'Qualify and route new case inquiries automatically',
        description:
            'Prospective clients rarely call outside business hours, but they will fill out a quick guided form if it feels responsive. AIPA asks the right qualifying questions for your practice areas, filters out inquiries you can’t take, and delivers every qualified lead straight to the right attorney.',
        bullets: [
            'Practice-area-specific qualifying questions, configured once',
            'Automatic filtering of inquiries outside your case criteria',
            'Qualified leads routed to the right attorney immediately',
        ],
    },
    {
        slug: 'education',
        icon: GraduationCap,
        name: 'Education',
        tagline: 'Help prospective students get answers and enroll faster',
        description:
            'Prospective students and parents have a lot of questions — tuition, programs, deadlines — and slow answers cost enrollments. AIPA answers those questions instantly and guides prospects toward scheduling a tour or info session, turning your website into an always-on admissions assistant.',
        bullets: [
            'Instant answers on programs, tuition, and deadlines',
            'Tour and info-session scheduling built into the conversation',
            'Every inquiry scored and routed to your admissions team',
        ],
    },
    {
        slug: 'financial-services',
        icon: Landmark,
        name: 'Financial Services',
        tagline: 'Answer account questions and route leads securely',
        description:
            'Financial services clients expect fast, accurate answers without compromising on trust. AIPA handles common account and service questions, qualifies new-client inquiries by need, and routes everything else straight to a licensed advisor — with every conversation logged for compliance.',
        bullets: [
            'Common account and service questions answered instantly',
            'New-client inquiries qualified by need before handoff',
            'Every conversation logged for compliance and follow-up',
        ],
    },
];

/** A shared "big image" preview for the Industries mega-menu and hero — a large branded gradient panel with the industry's icon, since no per-industry photography exists in this codebase. */
export function IndustryPreviewVisual({ icon: Icon, size = 'sm' }: { icon: LucideIcon; size?: 'sm' | 'lg' }) {
    const large = size === 'lg';

    return (
        <div
            className={`relative flex w-full items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#1e3a5f] to-[#2563eb] ${large ? 'h-72 max-w-md' : 'h-40'}`}
        >
            <div className={`absolute -right-8 -top-8 rounded-full bg-white/10 blur-2xl ${large ? 'size-48' : 'size-32'}`} />
            <div className={`absolute -bottom-10 -left-8 rounded-full bg-white/10 blur-2xl ${large ? 'size-48' : 'size-32'}`} />
            <div
                className={`relative flex items-center justify-center rounded-2xl bg-white/15 backdrop-blur ${large ? 'size-28' : 'size-16'}`}
            >
                <Icon className={large ? 'size-14 text-white' : 'size-8 text-white'} />
            </div>
        </div>
    );
}
